import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  VStack,
  Container,
  Heading,
  Text,
  useToast,
  Alert,
  AlertIcon,
  FormHelperText,
  FormErrorMessage,
} from '@chakra-ui/react';
import { useProfile } from '../hooks/useProfile';

export default function ProfileSetup() {
  const navigate = useNavigate();
  const toast = useToast();
  const { createProfile, error: profileError } = useProfile();
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    phone_number: '',
    is_administrator: false,
    is_message_sender: false,
    is_superadmin: false,
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === 'phone_number') {
      // Only allow digits and limit to 10 characters
      const digits = value.replace(/\D/g, '').slice(0, 10);
      setFormData(prev => ({
        ...prev,
        [name]: digits
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.first_name.trim()) newErrors.first_name = 'First name is required';
    if (!formData.last_name.trim()) newErrors.last_name = 'Last name is required';
    
    if (formData.phone_number) {
      const cleanPhone = formData.phone_number.replace(/\D/g, '');
      if (cleanPhone.length !== 10) {
        newErrors.phone_number = 'Phone number must be exactly 10 digits';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      // Format phone number with +1 prefix if it exists
      const profileData = {
        ...formData,
        phone_number: formData.phone_number ? `+1${formData.phone_number}` : null,
        full_name: `${formData.first_name.trim()} ${formData.last_name.trim()}`
      };

      const { error } = await createProfile(profileData);
      
      if (error) throw error;

      toast({
        title: 'Profile created successfully!',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });

      navigate('/profile');
    } catch (error) {
      toast({
        title: 'Error creating profile',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxW="container.sm" py={8}>
      <VStack spacing={8} align="stretch">
        <Box textAlign="center">
          <Heading size="lg" mb={2}>Complete Your Profile</Heading>
          <Text color="gray.600">Please provide your information to complete your profile setup.</Text>
        </Box>

        {profileError && (
          <Alert status="error">
            <AlertIcon />
            {profileError}
          </Alert>
        )}

        <Box as="form" onSubmit={handleSubmit}>
          <VStack spacing={4}>
            <FormControl isRequired isInvalid={errors.first_name}>
              <FormLabel>First Name</FormLabel>
              <Input
                name="first_name"
                value={formData.first_name}
                onChange={handleInputChange}
                placeholder="Enter your first name"
              />
              {errors.first_name && (
                <FormErrorMessage>{errors.first_name}</FormErrorMessage>
              )}
            </FormControl>

            <FormControl isRequired isInvalid={errors.last_name}>
              <FormLabel>Last Name</FormLabel>
              <Input
                name="last_name"
                value={formData.last_name}
                onChange={handleInputChange}
                placeholder="Enter your last name"
              />
              {errors.last_name && (
                <FormErrorMessage>{errors.last_name}</FormErrorMessage>
              )}
            </FormControl>

            <FormControl isInvalid={errors.phone_number}>
              <FormLabel>Phone Number</FormLabel>
              <Input
                name="phone_number"
                value={formData.phone_number}
                onChange={handleInputChange}
                placeholder="Enter your phone number"
                type="tel"
              />
              <FormHelperText>
                Enter a 10-digit number (e.g., 1234567890). Country code (+1) will be added automatically.
              </FormHelperText>
              {errors.phone_number && (
                <FormErrorMessage>{errors.phone_number}</FormErrorMessage>
              )}
            </FormControl>

            <Button
              type="submit"
              colorScheme="blue"
              width="full"
              isLoading={loading}
              loadingText="Creating Profile..."
            >
              Create Profile
            </Button>
          </VStack>
        </Box>
      </VStack>
    </Container>
  );
}
